import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaBox, FaCalendarAlt, FaExclamationCircle, FaCheckCircle, FaSearch, FaTimes } from 'react-icons/fa';
import { norm } from '../utils';
import { dataCompleta } from './etiquetaValidade';

/**
 * @param {boolean} embutido quando true, some o cabecalho proprio e as margens
 *   de pagina — a tela de Validade ja tem os dois, e dois titulos empilhados
 *   fariam parecer que sao duas telas dentro de uma.
 */
const BatchReport = ({ embutido = false }) => {
    const [lotes, setLotes] = useState([]);
    const [filtroProduto, setFiltroProduto] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'table'
    const [filtroStatus, setFiltroStatus] = useState('todos'); // 'todos', 'vencido', 'proximo', 'ok'
    const [isMobile, setIsMobile] = useState(false);

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth <= 768) {
                setViewMode('cards');
            } else {
                setViewMode('table');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        carregarLotes();
    }, []);

    const carregarLotes = async () => {
        try {
            setLoading(true);
            const response = await api.get('/lotes');
            setLotes(response.data);
            setError(null);
        } catch (error) {
            console.error("Erro ao buscar lotes:", error);
            setError("Erro ao carregar lotes");
        } finally {
            setLoading(false);
        }
    };

    // Determinar status do lote
    const obterStatusLote = (diasParaVencer) => {
        if (diasParaVencer <= 0) return 'vencido';
        if (diasParaVencer <= 7) return 'proximo';
        return 'ok';
    };

    // Obter cor baseada no status
    const obterCorStatus = (status) => {
        switch (status) {
            case 'vencido':
                return {
                    bg: 'bg-red-100',
                    text: 'text-red-700',
                    border: 'border-red-300',
                    icon: 'text-red-500'
                };
            case 'proximo':
                return {
                    bg: 'bg-yellow-100',
                    text: 'text-yellow-700',
                    border: 'border-yellow-300',
                    icon: 'text-yellow-500'
                };
            default:
                return {
                    bg: 'bg-green-100',
                    text: 'text-green-700',
                    border: 'border-green-300',
                    icon: 'text-green-500'
                };
        }
    };

    // Filtra e ordena os lotes
    const lotesFiltrados = lotes
        .filter(lote => 
            filtroProduto === '' || norm(lote.produto_nome).includes(norm(filtroProduto))
        )
        .filter(lote => {
            if (filtroStatus === 'todos') return true;
            const status = obterStatusLote(lote.dias_para_vencer);
            return status === filtroStatus;
        })
        .sort((a, b) => new Date(a.validade) - new Date(b.validade));

    // Componente Card para Lote
    const LoteCard = ({ lote }) => {
        const status = obterStatusLote(lote.dias_para_vencer);
        const cores = obterCorStatus(status);

        return (
            <div className={`rounded-lg border ${cores.border} ${cores.bg} p-4 mb-4 shadow-sm`}>
                <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{lote.produto_nome}</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                            <span>Lote: {lote.numero_lote}</span>
                            <span className="text-gray-400">•</span>
                            <span>{lote.quantidade} unidades</span>
                        </div>
                    </div>
                    <div className={`p-2 rounded-full ${cores.bg}`}>
                        {status === 'vencido' ? (
                            <FaExclamationCircle className={cores.icon} />
                        ) : status === 'proximo' ? (
                            <FaCalendarAlt className={cores.icon} />
                        ) : (
                            <FaCheckCircle className={cores.icon} />
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                        <div className="text-xs text-gray-500 mb-1">Validade</div>
                        <div className="font-medium">{dataCompleta(lote.validade)}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">Status</div>
                        <div className={`font-bold ${cores.text}`}>
                            {status === 'vencido' ? 'VENCIDO' : 
                             status === 'proximo' ? `${lote.dias_para_vencer} dias` : 
                             `${lote.dias_para_vencer} dias`}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Contadores para os badges
    const contadores = {
        vencido: lotes.filter(l => obterStatusLote(l.dias_para_vencer) === 'vencido').length,
        proximo: lotes.filter(l => obterStatusLote(l.dias_para_vencer) === 'proximo').length,
        ok: lotes.filter(l => obterStatusLote(l.dias_para_vencer) === 'ok').length,
        total: lotes.length
    };

    if (loading) {
        return (
            <div className="max-w-full md:max-w-5xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                    <p className="mt-2 text-gray-600">Carregando lotes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-full md:max-w-5xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <FaExclamationCircle className="mx-auto text-red-500 mb-2" size={32} />
                    <p className="text-red-600 font-medium">{error}</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={embutido
            ? ''
            : 'max-w-full md:max-w-5xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg'}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <div>
                    {!embutido && (
                      <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-2 md:mb-0">
                          Relatório de Controle de Lotes
                      </h2>
                    )}
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        <span className="bg-gray-100 px-2 py-1 rounded">
                            Total: {contadores.total}
                        </span>
                        {contadores.vencido > 0 && (
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                                {contadores.vencido} vencido{contadores.vencido > 1 ? 's' : ''}
                            </span>
                        )}
                        {contadores.proximo > 0 && (
                            <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                {contadores.proximo} próximo{contadores.proximo > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                </div>

                {!isMobile && (
                    <div className="flex gap-2 mt-4 md:mt-0">
                        <button
                            onClick={() => setViewMode('table')}
                            className={`px-4 py-2 rounded-lg ${viewMode === 'table' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                            Tabela
                        </button>
                        <button
                            onClick={() => setViewMode('cards')}
                            className={`px-4 py-2 rounded-lg ${viewMode === 'cards' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                            Cards
                        </button>
                    </div>
                )}
            </div>

            {/* Filtros */}
            <div className="mb-4 space-y-3">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Filtrar por Produto..."
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        value={filtroProduto}
                        onChange={(e) => setFiltroProduto(e.target.value)}
                    />
                    {filtroProduto && (
                        <button
                            onClick={() => setFiltroProduto('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setFiltroStatus('todos')}
                        className={`px-4 py-2 rounded-lg ${filtroStatus === 'todos' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Todos
                    </button>
                    <button
                        onClick={() => setFiltroStatus('vencido')}
                        className={`px-4 py-2 rounded-lg ${filtroStatus === 'vencido' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Vencidos
                    </button>
                    <button
                        onClick={() => setFiltroStatus('proximo')}
                        className={`px-4 py-2 rounded-lg ${filtroStatus === 'proximo' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Próximos de Vencer
                    </button>
                    <button
                        onClick={() => setFiltroStatus('ok')}
                        className={`px-4 py-2 rounded-lg ${filtroStatus === 'ok' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        OK
                    </button>
                </div>
            </div>

            {/* Visualização Condicional */}
            {lotesFiltrados.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <FaBox className="mx-auto mb-2" size={24} />
                    <p>Nenhum lote encontrado com os filtros selecionados</p>
                </div>
            ) : viewMode === 'table' ? (
                <div className="overflow-x-auto -mx-4 md:mx-0">
                    <div className="inline-block min-w-full align-middle px-4 md:px-0">
                        <table className="min-w-full border-collapse border border-gray-300 font-light">
                            <thead>
                                <tr style={{ backgroundColor: "#5B4740", color: "#FFF7F5" }}>
                                    <th className="p-2 text-left">Produto</th>
                                    <th className="p-2 text-center md:table-cell hidden">Lote</th>
                                    <th className="p-2 text-center">Qtd</th>
                                    <th className="p-2 text-center">Validade</th>
                                    <th className="p-2 text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lotesFiltrados.map(lote => {
                                    const status = obterStatusLote(lote.dias_para_vencer);
                                    const cores = obterCorStatus(status);
                                    
                                    return (
                                        <tr key={lote.id} 
                                            className={`border border-gray-300 ${
                                                status === 'vencido' ? 'bg-red-50' : 
                                                status === 'proximo' ? 'bg-yellow-50' : ''
                                            }`}>
                                            <td className="p-2">
                                                <div>
                                                    <div className="font-medium">{lote.produto_nome}</div>
                                                    <div className="text-xs text-gray-500 md:hidden">
                                                        Lote: {lote.numero_lote}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-2 text-center md:table-cell hidden">{lote.numero_lote}</td>
                                            <td className="p-2 text-center">{lote.quantidade}</td>
                                            <td className="p-2 text-center text-sm">{dataCompleta(lote.validade)}</td>
                                            <td className={`p-2 text-center font-bold ${cores.text}`}>
                                                {status === 'vencido' ? 'VENCIDO' : 
                                                 `${lote.dias_para_vencer}d`}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {lotesFiltrados.map((lote) => (
                        <LoteCard key={lote.id} lote={lote} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default BatchReport;